/*
This is a basic file upload, without any dependecy from 3rd party like uppy
 */

import React from 'react'
import isEmpty from 'lodash/isEmpty'
import type { File } from '@ztechapps/common.contract'
import { fetch } from '@ztechapps/common.utils'

export type UseFileUploadProps = {
  uri?: string
}

export type UseFilesUploadResult = [(files: any, options?: any) => Promise<File>, { loading: boolean; error: any }]

export const useFileUpload = (props?: UseFileUploadProps): UseFilesUploadResult => {
  const uri = props?.uri ?? '/api/file/upload'
  const [error, setError] = React.useState(null)
  const [loading, setLoading] = React.useState(false)

  const uploadFiles = React.useCallback(
    async (file, _options) => {
      try {
        setLoading(true)
        // Blob/File carry no enumerable props, so lodash isEmpty always reports them empty — accept
        // by byte size instead. Duck-typed, never `instanceof Blob`: a pasted file's prototype
        // belongs to the editor iframe's window, so the parent realm's instanceof reports it false
        // and every pasted upload would be rejected as empty.
        const byteSize = (file as Partial<Blob> | null | undefined)?.size
        if (typeof byteSize === 'number' ? byteSize === 0 : isEmpty(file)) {
          throw new Error('Please choose a file to be uploaded.')
        }

        const formData = new FormData()
        formData.append('file', file)

        //TODO: Use the useFetch handler
        //upload the files
        const response: Response = await fetch(
          uri,
          {
            method: 'POST',
            body: formData,
          },
          { lean: true, includeDefaultOptions: false }
        )

        const status = response.status
        if (status >= 400) {
          throw new Error(await response.text())
        }
        //set the result as to chatAttachment state
        return await response.json()
      } catch (e) {
        setError(e)
        //rethrow it
        throw e
      } finally {
        setLoading(false)
      }
    },
    [uri]
  )

  return [uploadFiles, { loading, error }]
}
